// src/pages/GestionCajas.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// 💰 GESTIÓN DE CAJAS - ESTILO eFIT
// Apertura y cierre de caja con diseño profesional
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import Swal from "sweetalert2";
import { useFiscal } from '../hooks/useFiscal';
import { imprimirCuadreCierre } from '../utils/cuadre.utils';

const GestionCajas = () => {
  const fiscal = useFiscal();
  const [cajaActiva, setCajaActiva] = useState(null);
  const [tieneCaja, setTieneCaja] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resumenActual, setResumenActual] = useState(null);
  const [cajasFisicas, setCajasFisicas] = useState([]);
  const [config, setConfig] = useState({});
  const [cuadreModal, setCuadreModal] = useState(null); // { caja, resumen }
  const navigate = useNavigate();

  useEffect(() => {
    verificarCajaActiva();
    cargarCajasFisicas();
    api.get('/configuracion').then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const verificarCajaActiva = async () => {
    try {
      const res = await api.get("/cajas/activa");
      setCajaActiva(res.data.caja);
      setTieneCaja(res.data.tiene_caja);
      setResumenActual(res.data.resumen_actual);
    } catch (error) {
      if (error.response?.status === 404) {
        setTieneCaja(false);
        setCajaActiva(null);
        setResumenActual(null);
      } else {
        console.error("Error al verificar caja:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const cargarCajasFisicas = async () => {
    try {
      const res = await api.get("/cajas-fisicas");
      setCajasFisicas(res.data);
    } catch (error) {
      console.error("Error al cargar cajas físicas:", error);
    }
  };

  const handleAbrirCaja = async () => {
    const { value: formValues } = await Swal.fire({
      title: '<span style="color: #1e3a8a;">🔓 Abrir Caja</span>',
      html: `
        <div style="text-align: left; padding: 10px 0;">
          <label style="display: block; margin-bottom: 8px; font-weight: 600; color: #374151;">
            📦 Selecciona la Caja: <span style="color: #ef4444;">*</span>
          </label>
          <select id="caja_fisica_id" class="swal2-select" style="width: 100%; padding: 12px; border-radius: 10px; border: 2px solid #e2e8f0; font-size: 14px;">
            <option value="">-- Selecciona una caja --</option>
            ${cajasFisicas.map(caja => `
              <option value="${caja.id}" ${caja.disponibilidad === 'ocupada' ? 'disabled' : ''}>
                ${caja.numero} ${caja.nombre ? '- ' + caja.nombre : ''} 
                ${caja.disponibilidad === 'ocupada' ? '(🔒 Ocupada)' : '(✅ Disponible)'}
              </option>
            `).join('')}
          </select>
          
          <label style="display: block; margin-top: 20px; margin-bottom: 8px; font-weight: 600; color: #374151;">
            💵 Monto Inicial: <span style="color: #ef4444;">*</span>
          </label>
          <input id="monto_inicial" type="number" step="0.01" class="swal2-input" placeholder="0.00" 
            style="width: 100%; padding: 12px; border-radius: 10px; border: 2px solid #e2e8f0; font-size: 16px; font-weight: 600; text-align: center; margin: 0;">
          
          <label style="display: block; margin-top: 20px; margin-bottom: 8px; font-weight: 600; color: #374151;">
            📝 Observaciones (opcional):
          </label>
          <textarea id="observaciones" placeholder="Notas sobre la apertura..."
            style="width: 100%; padding: 12px; border-radius: 10px; border: 2px solid #e2e8f0; font-size: 14px; min-height: 80px; margin: 0; resize: none; box-sizing: border-box; font-family: inherit;"></textarea>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "🔓 Abrir Caja",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#64748b",
      width: '500px',
      stopKeydownPropagation: false,
      preConfirm: () => {
        const cajaFisicaId = document.getElementById("caja_fisica_id").value;
        const monto = document.getElementById("monto_inicial").value;
        const obs = document.getElementById("observaciones").value;

        if (!cajaFisicaId) {
          Swal.showValidationMessage("⚠️ Debes seleccionar una caja");
          return false;
        }

        if (!monto || parseFloat(monto) < 0) {
          Swal.showValidationMessage("⚠️ Ingresa un monto inicial válido");
          return false;
        }

        return {
          caja_fisica_id: parseInt(cajaFisicaId),
          monto_inicial: parseFloat(monto),
          observaciones: obs,
        };
      },
    });

    if (formValues) {
      try {
        setLoading(true);
        const res = await api.post("/cajas/abrir", formValues);

        Swal.fire({
          icon: "success",
          title: "¡Caja Abierta!",
          html: `
            <div style="text-align: left; padding: 10px;">
              <p style="margin: 8px 0;"><strong>📦 Caja:</strong> ${res.data.caja_fisica.numero}</p>
              <p style="margin: 8px 0;"><strong>🔖 Código:</strong> ${res.data.caja.codigo}</p>
              <p style="margin: 8px 0; color: #10b981; font-size: 18px;"><strong>💵 Monto inicial:</strong> RD$${parseFloat(formValues.monto_inicial).toFixed(2)}</p>
            </div>
          `,
          confirmButtonColor: "#3b82f6",
        });

        verificarCajaActiva();
        cargarCajasFisicas();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.error || "No se pudo abrir la caja",
          confirmButtonColor: "#3b82f6",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCerrarCaja = async () => {
    if (!cajaActiva) return;

    const montoEsperado =
      parseFloat(cajaActiva.monto_inicial) +
      parseFloat(resumenActual?.total_efectivo || 0);

    // ── Paso 1: ingresar monto contado ────────────────────────────────────
    const { value: montoIngresado } = await Swal.fire({
      title: '<span style="color: #1e3a8a;">🔒 Cerrar Caja</span>',
      html: `
        <div style="text-align: left; padding: 10px 0;">
          <div style="background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); padding: 20px; border-radius: 14px; margin-bottom: 20px; border: 2px solid #3b82f6;">
            <p style="margin: 8px 0; color: #1e3a8a;"><strong>💵 Monto inicial:</strong> RD$${parseFloat(cajaActiva.monto_inicial).toFixed(2)}</p>
            <p style="margin: 8px 0; color: #1e3a8a;"><strong>📊 Ventas en efectivo:</strong> RD$${parseFloat(resumenActual?.total_efectivo || 0).toFixed(2)}</p>
            <div style="border-top: 2px dashed #3b82f6; margin: 15px 0; padding-top: 15px;">
              <p style="margin: 0; color: #10b981; font-size: 20px; font-weight: 700;">
                <strong>✅ Monto esperado:</strong> RD$${montoEsperado.toFixed(2)}
              </p>
            </div>
          </div>
          <label style="display: block; margin-bottom: 8px; font-weight: 600; color: #dc2626;">
            💰 Monto Real en Caja: <span style="color: #ef4444;">*</span>
          </label>
        </div>
      `,
      input: 'number',
      inputValue: montoEsperado.toFixed(2),
      inputAttributes: { step: '0.01', min: '0', placeholder: '0.00' },
      inputLabel: '',
      customClass: { input: 'swal2-input' },
      showCancelButton: true,
      confirmButtonText: "Siguiente →",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      width: '500px',
      preConfirm: (val) => {
        if (!val && val !== 0) {
          Swal.showValidationMessage("⚠️ Ingresa el monto real");
          return false;
        }
        return parseFloat(val);
      },
    });

    if (montoIngresado === undefined) return;

    const diferencia = montoIngresado - montoEsperado;

    // ── Paso 2: observaciones (obligatorio si hay faltante) ───────────────
    const esFaltante = diferencia < 0;
    const { value: observaciones } = await Swal.fire({
      title: esFaltante
        ? '<span style="color:#dc2626;">⚠️ Justificar Faltante</span>'
        : '<span style="color:#1e3a8a;">📝 Observaciones</span>',
      html: esFaltante
        ? `<p style="color:#dc2626; margin-bottom:8px;">Faltante detectado: <strong>RD$${Math.abs(diferencia).toFixed(2)}</strong><br>Explica el motivo para continuar.</p>`
        : `<p style="color:#64748b; margin-bottom:8px;">Puedes agregar una nota sobre este cierre (opcional).</p>`,
      input: 'textarea',
      inputPlaceholder: esFaltante
        ? 'Ej: Cajero reportó billete faltante al inicio del turno...'
        : 'Notas sobre el cierre...',
      inputAttributes: { rows: 4 },
      showCancelButton: true,
      confirmButtonText: "🔒 Cerrar Caja",
      cancelButtonText: "← Volver",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      width: '500px',
      preConfirm: (obs) => {
        if (esFaltante && !obs?.trim()) {
          Swal.showValidationMessage("⚠️ La justificación es obligatoria cuando hay faltante");
          return false;
        }
        return obs || '';
      },
    });

    if (observaciones === undefined) return;

    const formValues = { monto_real: montoIngresado, observaciones, diferencia };

    if (formValues) {
      try {
        setLoading(true);
        const res = await api.post(`/cajas/${cajaActiva.id}/cerrar`, {
          monto_real: formValues.monto_real,
          observaciones: formValues.observaciones,
        });
        // Mostrar cuadre en pantalla — el cajero decide cuándo imprimir
        setCuadreModal({ caja: res.data.caja, resumen: res.data.resumen });
        verificarCajaActiva();
        cargarCajasFisicas();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.error || "No se pudo cerrar la caja",
          confirmButtonColor: "#3b82f6",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const formatearMoneda = (monto) => {
    return new Intl.NumberFormat("es-DO", {
      style: "currency",
      currency: fiscal.moneda?.codigo || "DOP",
    }).format(monto || 0);
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingContent}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Cargando gestión de cajas...</p>
        </div>
      </div>
    );
  }

  // ── Modal cuadre de cierre ────────────────────────────────────────────────
  const ModalCuadre = () => {
    if (!cuadreModal) return null;
    const { caja, resumen } = cuadreModal;
    const dif = Number(resumen.diferencia) || 0;
    const fmtM = (n) => `RD$${Number(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}`;
    const fmtFecha = (f) => f ? new Date(f).toLocaleString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

    const estadoColor  = dif === 0 ? '#10b981' : dif > 0 ? '#3b82f6' : '#ef4444';
    const estadoBg     = dif === 0 ? '#ecfdf5' : dif > 0 ? '#eff6ff' : '#fef2f2';
    const estadoLabel  = dif === 0 ? '✅ CUADRADO' : dif > 0 ? '💰 SOBRANTE' : '⚠️ FALTANTE';

    const row = (label, valor, bold = false, color = '#374151') => (
      <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0',
        borderBottom:'1px solid #f1f5f9', fontWeight: bold ? 700 : 400, color }}>
        <span>{label}</span><span>{valor}</span>
      </div>
    );

    return (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:2000,
        display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
        <div style={{ background:'white', borderRadius:'20px', width:'100%', maxWidth:'520px',
          maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>

          {/* Header */}
          <div style={{ background:'linear-gradient(135deg,#1e3a8a,#3b82f6)', padding:'20px 24px',
            borderRadius:'20px 20px 0 0', color:'white' }}>
            <div style={{ fontSize:'22px', fontWeight:800 }}>📋 Cuadre de Cierre</div>
            <div style={{ fontSize:'13px', opacity:0.85, marginTop:'4px' }}>
              {caja.caja_fisica_numero || caja.codigo} — {caja.usuario_apertura_nombre}
            </div>
          </div>

          <div style={{ padding:'20px 24px' }}>

            {/* Datos turno */}
            <div style={{ fontSize:'12px', color:'#64748b', marginBottom:'16px' }}>
              <span>⏰ Apertura: {fmtFecha(caja.fecha_apertura)}</span>
              <span style={{ marginLeft:'16px' }}>🔒 Cierre: {fmtFecha(caja.fecha_cierre)}</span>
            </div>

            {/* ══ SECCIÓN 1: CUADRE DE EFECTIVO ══════════════════════════════ */}
            <div style={{ border:'2px solid #1e3a8a', borderRadius:'12px', padding:'16px', marginBottom:'16px' }}>
              <div style={{ fontWeight:800, color:'#1e3a8a', marginBottom:'10px', fontSize:'14px', letterSpacing:'0.5px' }}>
                💵 SECCIÓN 1 — CUADRE DE EFECTIVO
              </div>
              <div style={{ fontSize:'11px', color:'#64748b', marginBottom:'10px' }}>
                Lo que el cajero debe tener físicamente en caja
              </div>
              {row('Monto inicial', fmtM(resumen.monto_inicial))}
              {row('+ Ventas efectivo', fmtM(resumen.ventas_efectivo))}
              {Number(resumen.ingresos_adicionales) > 0 && row('+ Ingresos extra', fmtM(resumen.ingresos_adicionales))}
              {Number(resumen.egresos) > 0 && row('− Egresos', fmtM(resumen.egresos))}
              <div style={{ borderTop:'2px solid #1e3a8a', margin:'8px 0' }} />
              {row('= Monto esperado', fmtM(resumen.monto_esperado), true)}
              {row('Monto contado', fmtM(resumen.monto_real), true)}
            </div>

            {/* ══ SECCIÓN 2: RESUMEN INFORMATIVO ═════════════════════════════ */}
            <div style={{ border:'2px solid #e2e8f0', borderRadius:'12px', padding:'16px', marginBottom:'16px', background:'#f8fafc' }}>
              <div style={{ fontWeight:800, color:'#475569', marginBottom:'10px', fontSize:'14px', letterSpacing:'0.5px' }}>
                📊 SECCIÓN 2 — RESUMEN INFORMATIVO
              </div>
              <div style={{ fontSize:'11px', color:'#64748b', marginBottom:'10px' }}>
                Verificar por separado — no afecta el cuadre de efectivo
              </div>

              {/* Tarjeta */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                padding:'8px 0', borderBottom:'1px solid #e2e8f0' }}>
                <div>
                  <span style={{ fontWeight:600, color:'#374151' }}>💳 Tarjeta / Datafono</span>
                  <div style={{ fontSize:'11px', color:'#6b7280', marginTop:'2px' }}>Verificar con reporte del datafono</div>
                </div>
                <span style={{ fontWeight:700, color:'#1e3a8a' }}>{fmtM(resumen.ventas_tarjeta)}</span>
              </div>

              {/* Transferencia */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                padding:'8px 0', borderBottom:'1px solid #e2e8f0' }}>
                <div>
                  <span style={{ fontWeight:600, color:'#374151' }}>🔄 Transferencia</span>
                  <div style={{ fontSize:'11px', color:'#6b7280', marginTop:'2px' }}>Verificar con estado de cuenta bancario</div>
                </div>
                <span style={{ fontWeight:700, color:'#1e3a8a' }}>{fmtM(resumen.ventas_transferencia)}</span>
              </div>

              {/* Crédito */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                padding:'8px 0', borderBottom:'1px solid #e2e8f0' }}>
                <div>
                  <span style={{ fontWeight:600, color:'#374151' }}>📋 Crédito</span>
                  <div style={{ fontSize:'11px', color:'#6b7280', marginTop:'2px' }}>Pendiente de cobro a clientes</div>
                </div>
                <span style={{ fontWeight:700, color: Number(resumen.ventas_credito) > 0 ? '#dc2626' : '#374151' }}>
                  {fmtM(resumen.ventas_credito)}
                </span>
              </div>

              {/* Total general */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                padding:'10px 0 0 0', marginTop:'4px' }}>
                <span style={{ fontWeight:800, color:'#1e3a8a', fontSize:'14px' }}>
                  📈 Total ventas del turno ({resumen.num_ventas} ventas)
                </span>
                <span style={{ fontWeight:800, color:'#1e3a8a', fontSize:'15px' }}>{fmtM(resumen.total_ventas)}</span>
              </div>
            </div>

            {/* Estado diferencia */}
            <div style={{ background: estadoBg, border:`2px solid ${estadoColor}`, borderRadius:'12px',
              padding:'16px', textAlign:'center', marginBottom:'14px' }}>
              <div style={{ fontSize:'24px', fontWeight:800, color: estadoColor }}>{estadoLabel}</div>
              <div style={{ fontSize:'20px', fontWeight:700, color: estadoColor, marginTop:'4px' }}>
                {dif > 0 ? '+' : ''}{fmtM(dif)}
              </div>
              {dif > 0 && (
                <div style={{ fontSize:'12px', color: estadoColor, marginTop:'6px', opacity:0.85 }}>
                  El monto sobrante debe ser entregado al supervisor.
                </div>
              )}
            </div>

            {/* Observaciones / Justificación */}
            {caja.observaciones_cierre && (
              <div style={{ background: dif < 0 ? '#fef2f2' : '#f8fafc',
                border: `1px solid ${dif < 0 ? '#fca5a5' : '#e2e8f0'}`,
                borderRadius:'12px', padding:'14px', marginBottom:'14px' }}>
                <div style={{ fontWeight:700, color: dif < 0 ? '#dc2626' : '#1e3a8a',
                  marginBottom:'6px', fontSize:'13px' }}>
                  {dif < 0 ? '⚠️ JUSTIFICACIÓN DEL FALTANTE' : '📝 OBSERVACIONES'}
                </div>
                <div style={{ fontSize:'13px', color:'#374151', lineHeight:'1.5' }}>
                  {caja.observaciones_cierre}
                </div>
              </div>
            )}

            {/* Botones */}
            <div style={{ display:'flex', gap:'12px', marginTop:'8px' }}>
              <button
                onClick={() => imprimirCuadreCierre(caja, resumen, config)}
                style={{ flex:1, padding:'13px', background:'#1e3a8a', color:'white', border:'none',
                  borderRadius:'10px', fontWeight:700, fontSize:'15px', cursor:'pointer' }}>
                🖨️ Imprimir Cuadre
              </button>
              <button
                onClick={() => setCuadreModal(null)}
                style={{ flex:1, padding:'13px', background:'#f1f5f9', color:'#475569', border:'none',
                  borderRadius:'10px', fontWeight:700, fontSize:'15px', cursor:'pointer' }}>
                ✅ Cerrar
              </button>
            </div>

          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <ModalCuadre />

      <div style={styles.mainContent}>
        {/* Sin caja abierta */}
        {!tieneCaja && (
          <div style={styles.noCajaCard}>
            <div style={styles.noCajaIcon}>🔐</div>
            <h2 style={styles.noCajaTitle}>No tienes caja abierta</h2>
            <p style={styles.noCajaText}>
              Para comenzar a registrar ventas, necesitas abrir una caja primero.
            </p>
            <button 
              onClick={handleAbrirCaja} 
              style={styles.btnAbrirGrande}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-4px)';
                e.target.style.boxShadow = '0 12px 32px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.3)';
              }}
            >
              🔓 Abrir Nueva Caja
            </button>
          </div>
        )}

        {/* Con caja abierta */}
        {tieneCaja && cajaActiva && (
          <>
            {/* Card de caja activa */}
            <div style={styles.cajaActivaCard}>
              <div style={styles.cajaActivaHeader}>
                <div>
                  <div style={styles.estadoBadge}>
                    <span style={styles.estadoPulse}></span>
                    🟢 Caja Abierta
                  </div>
                  <h2 style={styles.cajaActivaTitle}>
                    {cajaActiva.caja_fisica?.numero || "Caja Principal"}
                  </h2>
                  <p style={styles.cajaCodigo}>Código: {cajaActiva.codigo}</p>
                  <p style={styles.cajaActivaFecha}>
                    📅 Abierta: {new Date(cajaActiva.fecha_apertura).toLocaleString("es-DO")}
                  </p>
                </div>
                <div style={styles.accionesGrid}>
                  <button 
                    onClick={handleCerrarCaja} 
                    style={styles.btnCerrar}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 20px rgba(239, 68, 68, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.2)';
                    }}
                  >
                    🔒 Cerrar Caja
                  </button>
                </div>
              </div>
            </div>

            {/* Resumen de la caja */}
            <div style={styles.resumenGrid}>
              <div style={styles.resumenCard}>
                <div style={styles.resumenIconBox}>
                  <span style={styles.resumenIcon}>💵</span>
                </div>
                <div>
                  <div style={styles.resumenLabel}>Monto Inicial</div>
                  <div style={styles.resumenMonto}>
                    {formatearMoneda(cajaActiva.monto_inicial)}
                  </div>
                </div>
              </div>

              <div style={{...styles.resumenCard, background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', border: '2px solid #10b981'}}>
                <div style={{...styles.resumenIconBox, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>
                  <span style={styles.resumenIcon}>📊</span>
                </div>
                <div>
                  <div style={styles.resumenLabel}>Total Ventas</div>
                  <div style={{...styles.resumenMonto, color: '#059669'}}>
                    {formatearMoneda(resumenActual?.total_ventas)} 
                    <span style={styles.resumenCount}>({resumenActual?.num_ventas || 0})</span>
                  </div>
                </div>
              </div>

              <div style={styles.resumenCard}>
                <div style={{...styles.resumenIconBox, background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'}}>
                  <span style={styles.resumenIcon}>💳</span>
                </div>
                <div>
                  <div style={styles.resumenLabel}>Efectivo en Caja</div>
                  <div style={{...styles.resumenMonto, color: '#1e3a8a'}}>
                    {formatearMoneda(
                      parseFloat(cajaActiva.monto_inicial) +
                      parseFloat(resumenActual?.total_efectivo || 0)
                    )}
                  </div>
                </div>
              </div>

              <div style={styles.resumenCard}>
                <div style={{...styles.resumenIconBox, background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'}}>
                  <span style={styles.resumenIcon}>🏦</span>
                </div>
                <div>
                  <div style={styles.resumenLabel}>Otros Métodos</div>
                  <div style={{...styles.resumenMonto, color: '#6d28d9'}}>
                    {formatearMoneda(
                      (parseFloat(resumenActual?.total_ventas) || 0) -
                      (parseFloat(resumenActual?.total_efectivo) || 0)
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Movimientos recientes */}
            {cajaActiva.movimientos && cajaActiva.movimientos.length > 0 && (
              <div style={styles.movimientosCard}>
                <h3 style={styles.movimientosTitle}>
                  <span style={styles.movimientosIcon}>📋</span>
                  Movimientos Recientes
                </h3>
                <div style={styles.movimientosList}>
                  {cajaActiva.movimientos.slice(0, 5).map((mov) => (
                    <div key={mov.id} style={styles.movimientoItem}>
                      <div style={{
                        ...styles.movimientoIcono,
                        background: mov.tipo === "ingreso" 
                          ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' 
                          : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
                      }}>
                        {mov.tipo === "ingreso" ? "💰" : "💸"}
                      </div>
                      <div style={styles.movimientoInfo}>
                        <div style={styles.movimientoConcepto}>{mov.concepto}</div>
                        <div style={styles.movimientoFecha}>
                          {new Date(mov.fecha).toLocaleString("es-DO")}
                        </div>
                      </div>
                      <div style={{
                        ...styles.movimientoMonto,
                        color: mov.tipo === "ingreso" ? "#059669" : "#dc2626",
                      }}>
                        {mov.tipo === "ingreso" ? "+" : "-"}
                        {formatearMoneda(mov.monto)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Estilos de animación */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.95); }
        }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS eFIT - PALETA AZUL CORPORATIVA
// ═══════════════════════════════════════════════════════════════════════════════

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)",
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  loadingContainer: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
  },
  loadingContent: {
    textAlign: "center",
    color: "white",
  },
  spinner: {
    width: "48px",
    height: "48px",
    border: "4px solid rgba(255,255,255,0.3)",
    borderTopColor: "white",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    margin: "0 auto 20px",
  },
  loadingText: {
    fontSize: "16px",
    opacity: "0.9",
  },

  // Header
  header: {
    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
    padding: "28px 40px",
    boxShadow: "0 4px 20px rgba(30, 58, 138, 0.3)",
  },
  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    maxWidth: "1400px",
    margin: "0 auto",
    flexWrap: "wrap",
    gap: "20px",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
  },
  headerIconBox: {
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(10px)",
    padding: "16px",
    borderRadius: "16px",
    border: "1px solid rgba(255, 255, 255, 0.2)",
  },
  headerIcon: {
    fontSize: "36px",
  },
  title: {
    fontSize: "32px",
    fontWeight: "800",
    color: "white",
    margin: "0",
    letterSpacing: "-0.5px",
  },
  subtitle: {
    fontSize: "15px",
    color: "rgba(255, 255, 255, 0.85)",
    margin: "6px 0 0 0",
  },
  btnHistorial: {
    padding: "14px 28px",
    background: "rgba(255,255,255,0.15)",
    color: "white",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    backdropFilter: "blur(10px)",
  },

  // Main Content
  mainContent: {
    padding: "32px 40px",
    maxWidth: "1400px",
    margin: "0 auto",
  },

  // No Caja
  noCajaCard: {
    background: "white",
    borderRadius: "24px",
    padding: "80px 60px",
    textAlign: "center",
    boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
    border: "1px solid rgba(59, 130, 246, 0.1)",
  },
  noCajaIcon: {
    fontSize: "80px",
    marginBottom: "24px",
  },
  noCajaTitle: {
    fontSize: "28px",
    fontWeight: "800",
    color: "#1e293b",
    margin: "0 0 12px 0",
  },
  noCajaText: {
    fontSize: "16px",
    color: "#64748b",
    marginBottom: "32px",
    maxWidth: "400px",
    margin: "0 auto 32px",
    lineHeight: "1.6",
  },
  btnAbrirGrande: {
    padding: "20px 48px",
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "white",
    border: "none",
    borderRadius: "16px",
    fontSize: "18px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 6px 20px rgba(16, 185, 129, 0.3)",
    transition: "all 0.3s ease",
  },

  // Caja Activa
  cajaActivaCard: {
    background: "white",
    borderRadius: "20px",
    padding: "32px",
    marginBottom: "28px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    border: "2px solid #10b981",
  },
  cajaActivaHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "20px",
  },
  estadoBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 16px",
    background: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
    color: "#065f46",
    borderRadius: "24px",
    fontSize: "14px",
    fontWeight: "700",
    marginBottom: "12px",
  },
  estadoPulse: {
    width: "8px",
    height: "8px",
    background: "#10b981",
    borderRadius: "50%",
    animation: "pulse 1.5s ease-in-out infinite",
  },
  cajaActivaTitle: {
    fontSize: "28px",
    fontWeight: "800",
    color: "#1e293b",
    margin: "0 0 8px 0",
  },
  cajaCodigo: {
    fontSize: "14px",
    color: "#64748b",
    margin: "0 0 4px 0",
    fontWeight: "600",
  },
  cajaActivaFecha: {
    fontSize: "14px",
    color: "#64748b",
    margin: "0",
  },
  accionesGrid: {
    display: "flex",
    gap: "12px",
  },
  btnCerrar: {
    padding: "14px 28px",
    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    color: "white",
    border: "none",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(239, 68, 68, 0.2)",
    transition: "all 0.3s ease",
  },

  // Resumen Grid
  resumenGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px",
    marginBottom: "28px",
  },
  resumenCard: {
    background: "white",
    borderRadius: "18px",
    padding: "24px",
    display: "flex",
    alignItems: "center",
    gap: "20px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
    border: "1px solid rgba(59, 130, 246, 0.1)",
  },
  resumenIconBox: {
    width: "60px",
    height: "60px",
    borderRadius: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    flexShrink: 0,
  },
  resumenIcon: {
    fontSize: "28px",
  },
  resumenLabel: {
    fontSize: "13px",
    color: "#64748b",
    marginBottom: "6px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  resumenMonto: {
    fontSize: "26px",
    fontWeight: "800",
    color: "#1e293b",
  },
  resumenCount: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#64748b",
    marginLeft: "8px",
  },

  // Movimientos
  movimientosCard: {
    background: "white",
    borderRadius: "20px",
    padding: "28px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
    border: "1px solid rgba(59, 130, 246, 0.1)",
  },
  movimientosTitle: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "20px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "24px",
    margin: "0 0 24px 0",
  },
  movimientosIcon: {
    fontSize: "24px",
  },
  movimientosList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  movimientoItem: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "16px 20px",
    background: "#f8fafc",
    borderRadius: "14px",
    transition: "all 0.2s ease",
  },
  movimientoIcono: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    flexShrink: 0,
  },
  movimientoInfo: {
    flex: 1,
  },
  movimientoConcepto: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "4px",
  },
  movimientoFecha: {
    fontSize: "12px",
    color: "#64748b",
  },
  movimientoMonto: {
    fontSize: "18px",
    fontWeight: "800",
  },
};

export default GestionCajas;